import React, { useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  fetchNotificationCount,
  fetchNotificationsHistory,
} from 'src/thunks/user_notification';
import { fetchDiscussionDetail } from 'src/thunks/discussion';
import { NOTIFICATION_TYPE } from 'src/constants/app';
import {
  fetchHomeworkCommentDetail,
  fetchHomeworkComments,
} from 'src/thunks/homework_comment';
import { fetchHomeworkCommentReply } from 'src/thunks/homework_reply_comment';

const SocketContext = React.createContext<Socket | undefined>(undefined);
export const useSocket = () => React.useContext(SocketContext)!;

const SocketProvider: React.FC<{ socket: Socket }> = ({ children, socket }) => {
  const dispatch = useAppDispatch();

  const user_id = useAppSelector((state) => state.ui.app.me?.id);

  useEffect(() => {
    socket.on('notification', function (message) {
      const res = JSON.parse(message);

      if (
        res.payload?.parent_id &&
        [
          NOTIFICATION_TYPE.new_discussion_reply,
          NOTIFICATION_TYPE.new_discussion_mention,
        ].includes(res.type)
      ) {
        dispatch(fetchDiscussionDetail({ id: res.payload?.parent_id }));
      }

      if (
        res.payload?.parent_id &&
        [
          NOTIFICATION_TYPE.new_homework_reply,
          NOTIFICATION_TYPE.new_homework_comment,
          NOTIFICATION_TYPE.new_homework_mention,
        ].includes(res.type)
      ) {
        dispatch(fetchHomeworkCommentDetail({ id: res.payload?.parent_id }));
        dispatch(
          fetchHomeworkCommentReply({
            parent_id: res.payload?.parent_id,
            class_topic_id: res.payload.class_topic_id,
          }),
        );
      }

      if (
        res.payload?.user_assignment_file_id &&
        [
          NOTIFICATION_TYPE.new_homework_reply,
          NOTIFICATION_TYPE.new_homework_comment,
          NOTIFICATION_TYPE.new_homework_mention,
        ].includes(res.type)
      ) {
        dispatch(
          fetchHomeworkComments({
            user_id,
            class_topic_id: res.payload.class_topic_id,
            user_assignment_file_id: res.payload.user_assignment_file_id,
          }),
        );
      }

      dispatch(fetchNotificationsHistory({}));
      dispatch(fetchNotificationCount());
    });
  }, []);

  useEffect(() => {
    socket.connect();

    socket.on('connect', function () {
      socket.emit('subscribe', 'global');
      socket.emit('subscribe', `user:${user_id}`);
    });

    socket.on('connect_error', console.error);

    return () => {
      socket.off('connect');
      socket.off('notification');
      socket.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

export default SocketProvider;
